import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, subtract, type ReceiptDto } from '@gc/shared';

import { serviceChargeSign } from './checkoutPreview';

/**
 * The combined receipt (SPEC §10).
 *
 * One long ticket for the whole party: every device with its duration and rate,
 * then discounts, then the grand total. Styled at thermal-roll width so what is
 * on screen matches what comes out of the printer.
 *
 * Wave 3: the summary itemises the split discount — "Reservations 40% −X /
 * Cafe 10% −Y / Kitchen 5% −Z / Manual −W". A bucket whose percent is null (the
 * customer holds none) or whose amount is zero is omitted rather than printed
 * as "0%". Amounts are wrapped LTR so the minus binds to the number under RTL.
 */
export function ReceiptView({ receipt }: { receipt: ReceiptDto }) {
  const { t } = useTranslation();
  const symbol = receipt.currencySymbol;
  const money = (md: string) => formatIqd(fromMillidinars(md), { currencySymbol: symbol });

  // The generic discount row carries only what the split lines below do NOT
  // itemise: per-session and per-order discounts already shown on their lines.
  const itemisedElsewhereMd = [
    receipt.timeDiscountMd,
    receipt.cafeDiscountMd,
    receipt.kitchenDiscountMd,
    receipt.manualDiscountMd,
  ].reduce(
    (remaining, part) => subtract(remaining, fromMillidinars(part)),
    fromMillidinars(receipt.discountMd),
  );

  /*
   * A bucket line shows when it has an AMOUNT, and names its percent only when a
   * percent applied. Gating on the percent — as this did while a bucket discount
   * could only come from the account — hides an amount a cashier typed, and the
   * money then reappears unlabelled in the generic residual row above.
   */
  const bucketLines = [
    {
      key: 'time',
      label: 'receipt.discountReservations',
      flat: 'receipt.discountReservationsFlat',
      percent: receipt.timeDiscountPercent,
      amountMd: receipt.timeDiscountMd,
    },
    {
      key: 'cafe',
      label: 'receipt.discountCafe',
      flat: 'receipt.discountCafeFlat',
      percent: receipt.cafeDiscountPercent,
      amountMd: receipt.cafeDiscountMd,
    },
    {
      key: 'kitchen',
      label: 'receipt.discountKitchen',
      flat: 'receipt.discountKitchenFlat',
      percent: receipt.kitchenDiscountPercent,
      amountMd: receipt.kitchenDiscountMd,
    },
  ].filter((line) => line.amountMd !== '0');

  return (
    <div className="mx-auto w-full max-w-xs bg-white p-4 font-mono text-[11px] leading-snug text-black">
      <div className="text-center">
        <div className="text-sm font-bold uppercase">{receipt.venueName}</div>
        {receipt.venueSlogan ? <div>{receipt.venueSlogan}</div> : null}
        {receipt.venueAddress ? <div>{receipt.venueAddress}</div> : null}
        {receipt.venuePhone ? <div>{receipt.venuePhone}</div> : null}
      </div>

      <div className="my-2 border-t border-dashed border-black" />

      <div className="flex justify-between">
        <span>{receipt.tabName}</span>
        <span>{receipt.businessDate}</span>
      </div>
      {/* The number a customer reads back to us — Bills finds it (owner,
          2026-09-23). Absent on a receipt from before numbering. */}
      {receipt.receiptNumber ? (
        <div className="font-bold" data-receipt-number>
          {t('serviceCharge.receiptNumber', { number: receipt.receiptNumber })}
        </div>
      ) : null}
      <div className="flex justify-between">
        <span>{t('bills.columns.receiptEditor')}</span>
        <span>{receipt.cashierName}</span>
      </div>
      <div>{new Date(receipt.issuedAt).toLocaleString()}</div>

      <div className="my-2 border-t border-dashed border-black" />

      {receipt.sessions.map((line) => (
        <div key={line.sessionId} className="mb-2">
          <div className="flex justify-between font-bold">
            <span>{line.deviceName}</span>
            <span>{line.invoiceNumber}</span>
          </div>
          <div className="flex justify-between">
            <span>
              {line.reservationType === 'HOURLY'
                ? `${line.duration} × ${money(line.hourlyPriceMd)}`
                : `${line.singleRounds + line.doubleRounds + line.threeBallRounds} × ${money(line.roundPriceMd)}`}
            </span>
            <span>{money(line.totalMd)}</span>
          </div>
          {line.discountMd !== '0' ? (
            <div className="flex justify-between">
              <span>{t('bills.columns.discount')}</span>
              <span>-{money(line.discountMd)}</span>
            </div>
          ) : null}
        </div>
      ))}

      {/* Cafe orders, grouped under where they were delivered (SPEC §5.3) —
          a party wants to see which PC the burger went to. */}
      {receipt.orders.map((order) => (
        <div key={order.orderId} className="mb-2">
          <div className="flex justify-between font-bold">
            <span>{order.destinationName}</span>
            <span>{order.invoiceNumber}</span>
          </div>
          {order.items.map((item, index) => (
            <div key={`${order.orderId}-${index}`} className="flex justify-between">
              <span>
                {item.quantity} × {item.name}
              </span>
              <span>{money(item.lineTotalMd)}</span>
            </div>
          ))}
          {order.discountMd !== '0' ? (
            <div className="flex justify-between">
              <span>{t('bills.columns.discount')}</span>
              <span>-{money(order.discountMd)}</span>
            </div>
          ) : null}
        </div>
      ))}

      <div className="my-2 border-t border-dashed border-black" />

      <div className="flex justify-between">
        <span>{t('common.total')}</span>
        <span>{money(receipt.subtotalMd)}</span>
      </div>
      {/* Added before any discount, so it sits above them — and not at all at
          0% or when the cashier removed it (owner: "if the service is 0% it
          should not be mentioned in the receipt at all"). */}
      {(receipt.serviceChargePercent ?? 0) > 0 && (receipt.serviceChargeMd ?? '0') !== '0' ? (
        <div className="flex justify-between" data-service-charge>
          <span>{t('serviceCharge.line', { percent: receipt.serviceChargePercent })}</span>
          <span dir="ltr">
            {serviceChargeSign(receipt.serviceChargeMd ?? '0')}
            {money(receipt.serviceChargeMd ?? '0')}
          </span>
        </div>
      ) : null}
      {itemisedElsewhereMd !== 0n ? (
        <div className="flex justify-between">
          <span>{t('bills.columns.discount')}</span>
          <span dir="ltr">-{money(itemisedElsewhereMd.toString())}</span>
        </div>
      ) : null}
      {bucketLines.map((line) => (
        <div key={line.key} className="flex justify-between">
          <span>
            {line.percent == null ? t(line.flat) : t(line.label, { percent: line.percent })}
          </span>
          <span dir="ltr">-{money(line.amountMd)}</span>
        </div>
      ))}
      {receipt.manualDiscountMd !== '0' ? (
        <div className="flex justify-between">
          <span>{t('receipt.discountManual')}</span>
          <span dir="ltr">-{money(receipt.manualDiscountMd)}</span>
        </div>
      ) : null}
      <div className="mt-1 flex justify-between border-t border-black pt-1 text-sm font-bold">
        <span>{t('reports.overall.netProfit')}</span>
        <span>{money(receipt.grandTotalMd)}</span>
      </div>

      {receipt.tenderedMd ? (
        <>
          <div className="mt-1 flex justify-between">
            <span>{t('checkout.tendered')}</span>
            <span>{money(receipt.tenderedMd)}</span>
          </div>
          <div className="flex justify-between">
            <span>{t('checkout.change')}</span>
            <span>{money(receipt.changeMd ?? '0')}</span>
          </div>
        </>
      ) : null}

      {/* As the paper says it — cash and balance receipts never did. */}
      {receipt.method === 'ELECTRONIC' ? (
        <div data-paid-by className="mt-1 text-center">
          {t('tender.paidBy', {
            provider: receipt.electronicProviderLabel ?? t('payments.electronic'),
          })}
        </div>
      ) : null}

      <div className="mt-3 text-center">{t('checkout.thankYou')}</div>
    </div>
  );
}
